import React, { useMemo } from "react";
import {
  Box,
  Card,
  Stack,
  Typography,
  Button,
  IconButton,
  Chip,
  Avatar,
  Divider,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetPermissionMatrixModulesQuery,
  useGetRolePermissionQuery,
  useGetUserRoleQuery,
  useGetUserInfoOnyQuery,
} from "../../../Api/attoDeskApi";
import { IUserAdmin, IUserRole } from "../../../Api/Interface/api.interface";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import PermissionMatrix from "../Components/PermissionMatrix";
import useUser from "../../../Core/hook/useUser";
import { Privileges } from "../../../Core/Enum/enum";
import {
  MatrixGroup,
  MatrixFlag,
  ACTION_KEYS,
} from "../Components/permissionUtils";
import { categoryIcon } from "../Components/PermissionMatrix";

const ViewRole: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const roleId = id ? Number(id) : undefined;
  const { userHasRight } = useUser();
  const canEdit = userHasRight(Privileges.UserView);

  const { data: rolesResp, isLoading: rolesLoading } = useGetUserRoleQuery({
    searchText: "",
  });
  const { data: modulesResp, isLoading: modulesLoading } =
    useGetPermissionMatrixModulesQuery();
  const { data: rolePermResp, isFetching: permsLoading } =
    useGetRolePermissionQuery({ roleId } as any, { skip: !roleId });
  const { data: userData } = useGetUserInfoOnyQuery({ searchText: "" });

  const roles: IUserRole[] = Array.isArray(rolesResp?.data)
    ? (rolesResp?.data as IUserRole[])
    : [];
  const role = roles.find((r) => Number(r.id) === roleId);

  const groups: MatrixGroup[] = (modulesResp as any)?.data?.groups ?? [];
  const flags: MatrixFlag[] = (modulesResp as any)?.data?.flags ?? [];

  const assigned = useMemo(() => {
    const rows: any[] = (rolePermResp as any)?.data ?? [];
    return new Set<number>(rows.filter((p) => p.isAssigned).map((p) => p.id));
  }, [rolePermResp]);

  const userList = ((userData?.data as IUserAdmin[]) || []).filter(
    (u) => String(u.user_role) === String(roleId)
  );

  // Per-category summary derived from the SAME matrix data the section
  // below renders — so the summary strip and the detail cards can never
  // disagree about which categories a role touches or how many
  // permissions are granted in each.
  const categorySummary = useMemo(() => {
    const flagsByCat: Record<string, MatrixFlag[]> = {};
    flags.forEach((f) => {
      const k = f.category || "Other";
      (flagsByCat[k] = flagsByCat[k] || []).push(f);
    });
    return groups
      .map((g) => {
        const catFlags = flagsByCat[g.category] || [];
        let granted = 0;
        let total = catFlags.length;
        let hasWrite = false;
        g.modules.forEach((m) => {
          ACTION_KEYS.forEach((a) => {
            const id = m.actions[a];
            if (id) total += 1;
            if (id && assigned.has(id)) {
              granted += 1;
              if (a !== "view") hasWrite = true;
            }
          });
        });
        catFlags.forEach((f) => {
          if (assigned.has(f.id)) { granted += 1; hasWrite = true; }
        });
        return { category: g.category, granted, total, hasWrite };
      })
      .filter((c) => c.granted > 0)
      .sort((a, b) => b.granted - a.granted);
  }, [groups, flags, assigned]);

  const loading = rolesLoading || modulesLoading || permsLoading;

  if (!loading && !role) {
    return (
      <Box sx={{ p: 4 }}>
        <Card sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Role not found
          </Typography>
          <Typography sx={{ color: "#64748b", mb: 2 }}>
            The role you're looking for doesn't exist or was deleted.
          </Typography>
          <Button variant="outlined" onClick={() => navigate("/user/role/list")}>
            Back to roles
          </Button>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ background: "#f8fafc", minHeight: "100%" }}>
      {/* Header */}
      <Card
        sx={{
          borderRadius: 0,
          boxShadow: "none",
          borderBottom: "1px solid #e2e8f0",
          background: "#fff",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ px: 3, py: 2 }}
        >
          <IconButton onClick={() => navigate("/user/role/list")} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Avatar sx={{ bgcolor: "#eef2ff", color: "#4f46e5" }}>
            <AdminPanelSettingsOutlinedIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
              {role ? toProperCase(role.roleName) : "Loading…"}
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              Role details — read-only
            </Typography>
          </Box>
          {canEdit && role && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/user/role/${role.id}/edit`)}
            >
              Edit
            </Button>
          )}
        </Stack>
      </Card>

      <Box sx={{ p: 3 }}>
        {/* Meta chips */}
        {role && (
          <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1} sx={{ mb: 2 }}>
            <Chip
              icon={<KeyOutlinedIcon />}
              label={`${assigned.size} permissions`}
              sx={{ background: "#ecfdf5", color: "#047857" }}
            />
            <Chip
              icon={<GroupsOutlinedIcon />}
              label={
                userList.length === 0
                  ? "No users assigned"
                  : `${userList.length} user${userList.length === 1 ? "" : "s"}`
              }
              sx={{
                background: userList.length > 0 ? "#eef2ff" : "#f1f5f9",
                color: userList.length > 0 ? "#3730a3" : "#64748b",
              }}
            />
          </Stack>
        )}

        {/* Batch 23 — one-glance summary. Answers "what can this role do?"
            without scrolling: a card per touched category with count and
            read-only / includes-write shape. Nothing is repeated below;
            the matrix beneath renders the same numbers as a drill-down. */}
        {role && !loading && (
          <Card
            sx={{
              mb: 3,
              border: "1px solid #e2e8f0",
              boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography sx={{ fontWeight: 700, color: "#0f172a", mb: 1.25, fontSize: 14 }}>
              Access summary
            </Typography>
            {categorySummary.length === 0 ? (
              <Typography sx={{ color: "#94a3b8", fontSize: 13 }}>
                This role has no permissions granted.
              </Typography>
            ) : (
              <Box sx={{
                display: "grid",
                gap: 1.25,
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)" },
              }}>
                {categorySummary.map((c) => (
                  <Box key={c.category} sx={{
                    p: 1.25, border: "1px solid #e2e8f0",
                    borderRadius: 1.5, background: "#f8fafc",
                    display: "flex", alignItems: "center", gap: 1,
                  }}>
                    {categoryIcon(c.category)}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#0f172a",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {c.category}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                        {c.granted} of {c.total} granted
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={c.hasWrite ? "Write" : "Read only"}
                      sx={{
                        height: 20, fontSize: 10, fontWeight: 700,
                        background: c.hasWrite ? "#fef3c7" : "#e0f2fe",
                        color:      c.hasWrite ? "#92400e" : "#075985",
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Card>
        )}

        {/* Users holding this role */}
        <Card
          sx={{
            mb: 3,
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
            borderRadius: 2,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ px: 2, py: 1.5, background: "#f8fafc" }}
          >
            <GroupsOutlinedIcon sx={{ color: "#475569" }} />
            <Typography sx={{ fontWeight: 700, color: "#0f172a", flex: 1 }}>
              Users with this role
            </Typography>
            <Chip size="small" label={userList.length} />
          </Stack>
          <Divider />
          {userList.length === 0 ? (
            <Typography sx={{ p: 2, color: "#94a3b8", textAlign: "center" }}>
              No users are assigned to this role.
            </Typography>
          ) : (
            <Stack divider={<Divider />}>
              {userList.map((u) => (
                <Stack
                  key={u.id}
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ px: 2, py: 1 }}
                >
                  <Avatar sx={{ width: 28, height: 28, bgcolor: "#f1f5f9" }}>
                    <PersonOutlineIcon fontSize="small" sx={{ color: "#475569" }} />
                  </Avatar>
                  <Typography sx={{ flex: 1, fontSize: 14, color: "#334155" }}>
                    {u.username}
                  </Typography>
                  {u.email && (
                    <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                      {u.email}
                    </Typography>
                  )}
                </Stack>
              ))}
            </Stack>
          )}
        </Card>

        <PermissionMatrix
          groups={groups}
          flags={flags}
          assigned={assigned}
          loading={loading}
          mode="view"
        />
      </Box>
    </Box>
  );
};

export default ViewRole;
